import React, { useEffect, useState, useCallback } from "react";
import { Plus, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, statusTone } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminUsers() {
  const { can } = useAuth();
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const sp = new URLSearchParams();
    if (role !== "all") sp.set("role", role);
    if (q) sp.set("q", q);
    api.get(`/users?${sp}`).then(({ data }) => setUsers(data.filter((u) => u.status !== "deleted")));
  }, [role, q]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (u, status) => {
    await api.put(`/users/${u.id}`, { status });
    toast.success(`User ${status}`); load();
  };

  return (
    <div data-testid="admin-users">
      <PageHeader title="User Management" subtitle={`${users.length} users`} action={can("users", "create") && <CreateUserDialog onDone={load} />} />
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input className="pl-9" placeholder="Search users..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="user-search" /></div>
        <Select value={role} onValueChange={setRole}><SelectTrigger className="w-44" data-testid="user-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Roles</SelectItem><SelectItem value="learner">Learners</SelectItem><SelectItem value="educator">Educators</SelectItem><SelectItem value="content_seller">Content Sellers</SelectItem><SelectItem value="sub_admin">Sub Admins</SelectItem><SelectItem value="admin">Admins</SelectItem></SelectContent></Select>
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-muted-foreground"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                <td className="p-4 font-medium text-navy">{u.name}</td>
                <td className="p-4 text-muted-foreground">{u.email}</td>
                <td className="p-4"><Badge tone="gray">{u.role?.replace("_", " ")}</Badge></td>
                <td className="p-4"><Badge tone={statusTone(u.status)}>{u.status}</Badge></td>
                <td className="p-4">
                  {can("users", "edit") && (
                    u.status === "active"
                      ? <Button size="sm" variant="outline" className="text-crimson" onClick={() => setStatus(u, "suspended")} data-testid={`suspend-${u.id}`}>Suspend</Button>
                      : <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => setStatus(u, "active")} data-testid={`activate-${u.id}`}>Activate</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreateUserDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "sub_admin", mobile: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const create = async () => {
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="viriks-navy text-white" data-testid="create-user-btn"><Plus size={16} className="mr-1" /> Create Internal User</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create Internal User</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cu-name" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="cu-email" /></div>
          <div><Label>Password</Label><Input value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="cu-password" /></div>
          <div><Label>Mobile</Label><Input value={f.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="cu-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="sub_admin">Sub Admin</SelectItem><SelectItem value="educator">Educator</SelectItem><SelectItem value="content_seller">Content Seller</SelectItem></SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button className="viriks-navy text-white" onClick={create} data-testid="cu-submit">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
